import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
    SettingOption,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import NodePort from '@appblocks/codegeneration/Nodes/NodePort';
import NodeParameter from '@appblocks/codegeneration/Nodes/NodeParameter';
import PlatformUtils from '@appblocks/platforms/PlatformUtils';

export default class LCDNode extends BaseNode {
    constructor() {
        super();
        this.name = 'LCD Print';
        this.id = 'node_lcd';
        this.linkedFeature = 'lcd';
        this.icon = 'CgScreen';

        this.inputs = [
            {
                name: 'Draw Text',
                portId: 'lcd_print',
                params: [
                    {
                        name: 'text',
                        dataType: TibboDataType.STRING,
                        default: '',
                    },
                ],
            },
        ];

        const settings: Setting[] = [
            {
                id: 'FONT',
                name: 'Font Size',
                settingType: SettingType.OPTION,
                required: true,
                default: 'LCD_STD_FONT',
                options: [
                    {
                        name: 'Standard Font',
                        value: 'LCD_STD_FONT',
                    },
                    {
                        name: 'Small Font',
                        value: 'LCD_SMALL_FONT',
                    },
                ],
            },
            {
                id: 'COLOR',
                name: 'Color',
                settingType: SettingType.COLOR,
                required: true,
                default: '#ffffff',
            },
            {
                id: 'BACKCOLOR',
                name: 'Background Color',
                settingType: SettingType.COLOR,
                required: true,
                default: '#000000',
            },
            {
                id: 'ALIGNMENT',
                name: 'Text Alignment',
                settingType: SettingType.OPTION,
                default: 'PL_LCD_TEXT_ALIGNMENT_TOP_LEFT',
                options: [
                    {
                        name: 'Top Left',
                        value: 'PL_LCD_TEXT_ALIGNMENT_TOP_LEFT',
                    },
                    {
                        name: 'Top Center',
                        value: 'PL_LCD_TEXT_ALIGNMENT_TOP_CENTER',
                    },
                    {
                        name: 'Top Right',
                        value: 'PL_LCD_TEXT_ALIGNMENT_TOP_RIGHT',
                    },
                    {
                        name: 'Middle Left',
                        value: 'PL_LCD_TEXT_ALIGNMENT_MIDDLE_LEFT',
                    },
                    {
                        name: 'Middle Center',
                        value: 'PL_LCD_TEXT_ALIGNMENT_MIDDLE_CENTER',
                    },
                    {
                        name: 'Middle Right',
                        value: 'PL_LCD_TEXT_ALIGNMENT_MIDDLE_RIGHT',
                    },
                    {
                        name: 'Bottom Left',
                        value: 'PL_LCD_TEXT_ALIGNMENT_BOTTOM_LEFT',
                    },
                    {
                        name: 'Bottom Center',
                        value: 'PL_LCD_TEXT_ALIGNMENT_BOTTOM_CENTER',
                    },
                    {
                        name: 'Bottom Right',
                        value: 'PL_LCD_TEXT_ALIGNMENT_BOTTOM_RIGHT',
                    },
                ],
            },
            {
                id: 'ORIENTATION',
                name: 'Text Orientation',
                settingType: SettingType.OPTION,
                default: 'PL_LCD_TEXT_ORIENTATION_0',
                options: [
                    {
                        name: 'At 0 Degrees',
                        value: 'PL_LCD_TEXT_ORIENTATION_0',
                    },
                    {
                        name: 'At 90 degrees',
                        value: 'PL_LCD_TEXT_ORIENTATION_90',
                    },
                    {
                        name: 'At 180 degrees',
                        value: 'PL_LCD_TEXT_ORIENTATION_180',
                    },
                    {
                        name: 'At 270 degrees',
                        value: 'PL_LCD_TEXT_ORIENTATION_270',
                    },
                ],
            },
            {
                id: 'VERTICAL_SPACING',
                name: 'Vertical Spacing',
                settingType: SettingType.NUMBER,
                default: '1',
                min: 0,
                max: 255,
                helpText: 'Specifies the gap, in pixels, between the lines of text output',
            },
            {
                id: 'HORIZONTAL_SPACING',
                name: 'Horizontal Spacing',
                settingType: SettingType.NUMBER,
                default: '1',
                min: 0,
                max: 255,
                helpText: 'Specifies the gap, in pixels, between characters of text output',
            },
            {
                id: 'PX',
                name: 'Print Area - X Position',
                settingType: SettingType.NUMBER,
                default: '0',
                min: 0,
                max: 320,
            },
            {
                id: 'PY',
                name: 'Print Area - Y Position',
                settingType: SettingType.NUMBER,
                default: '0',
                min: 0,
                max: 240,
            },
            {
                id: 'WIDTH',
                name: 'Print Area - Width',
                settingType: SettingType.NUMBER,
                default: '50',
                min: 0,
                max: 320,
            },
            {
                id: 'HEIGHT',
                name: 'Print Area - Height',
                settingType: SettingType.NUMBER,
                default: '20',
                min: 0,
                max: 240,
            },
        ];

        this.settings = this.settings.concat(settings);
    }

    getSettingValue(settingId: string): string {
        if (settingId === 'DISPLAY_NAME') {
            const value = super.getSettingValue(settingId);
            if (value !== '') {
                return value;
            }
            const project: ProjectConfiguration = this.project;
            const links = project.getNodeLinks(this, 'lcd_print');
            if (links.length > 0) {
                const link = links[0];
                const printstr = project.getNodeParameterValue(this, 'text');
                return `LCD Print ${printstr}`;
            }
        }
        return super.getSettingValue(settingId);
    }

    getName(): string {
        const project: ProjectConfiguration = this.project;
        const links = project.getNodeLinks(this, 'lcd_print');
        if (links.length > 0) {
            const link = links[0];
            const printstr = project.getNodeParameterValue(this, 'text');
            return `LCD Print ${printstr}`;
        }
        return this.name;
    }

    getCodeBlocks(): CodeBlock[] {
        const blocks: CodeBlock[] = [];
        const project: ProjectConfiguration = this.project;
        const FONT = this.getSettingValue('FONT');
        const COLOR = PlatformUtils.hexToTibboColor(this.getSettingValue('COLOR'));
        const BACKCOLOR = PlatformUtils.hexToTibboColor(this.getSettingValue('BACKCOLOR'));
        const ALIGNMENT = this.getSettingValue('ALIGNMENT');
        const ORIENTATION = this.getSettingValue('ORIENTATION');
        const VERTICAL_SPACING = this.getSettingValue('VERTICAL_SPACING');
        const HORIZONTAL_SPACING = this.getSettingValue('HORIZONTAL_SPACING');
        const X = this.getSettingValue('PX');
        const Y = this.getSettingValue('PY');
        const WIDTH = this.getSettingValue('WIDTH');
        const HEIGHT = this.getSettingValue('HEIGHT');

        const doneLinks = project.getNodeLinks(this, 'done');
        const doneBlock = doneLinks.length > 0 ? `${project.getCodeLocationStart()}${doneLinks[0].getSourceId()}${project.getCodeLocationEnd()}` : '';
        const links = project.getNodeLinks(this, 'lcd_print');
        for (let i = 0; i < links.length; i += 1) {
            const link = links[i];
            if (project.runtime === 'simulator') {
                blocks.push({
                    location: link.getSourceId(),
                    blockType: BlockType.CODE,
                    id: link.getID(),
                    template: `
                    ${doneBlock}`,
                });
            } else {
                blocks.push({
                    location: link.getSourceId(),
                    blockType: BlockType.CODE,
                    id: link.getID(),
                    template: `
    lcd.forecolor=${BACKCOLOR}
    lcd.fill(${X},${Y},${WIDTH},${HEIGHT})
    romfile.open(${FONT})
    lcd.setfont(romfile.offset)
    lcd.textorientation=${ORIENTATION}
    lcd.textverticalspacing=${VERTICAL_SPACING}
    lcd.texthorizontalspacing=${HORIZONTAL_SPACING}
    lcd.forecolor=${COLOR}
    lcd.backcolor=${BACKCOLOR}
    lcd.textalignment=${ALIGNMENT}
    lcd.printaligned(${project.getNodeParameter(link, 'text')}, ${X}, ${Y}, ${WIDTH}, ${HEIGHT})
    ${doneBlock}`,
                });
            }
        }

        return blocks;
    }

    getDetails(): string {
        return 'Draws text on the LCD screen';
    }
}
